import React, { useState, useEffect } from 'react';
import { FileText, Download, Share2, Mail, Copy, CheckCircle, Edit3 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Textarea } from './components/ui/textarea';
import { generateResumePdf, generateVerificationCode } from './lib/generateResumePdf';
import { PLATFORM_INFO } from './lib/csvImportUtils';
import {
  getDocs, getDoc, updateDoc, doc, collection, query, where,
} from 'firebase/firestore';
import { db } from './firebase';

/* ═══════════════════════════════════════════
   READER READING RÉSUMÉ
   Generates a downloadable PDF résumé for ARC applications.
═══════════════════════════════════════════ */

export default function ReaderResume({ readerData, isAdmin = false }) {
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);
  const [editingBio, setEditingBio] = useState(false);
  const [bio, setBio] = useState(readerData?.resumeBio || '');
  const [savingBio, setSavingBio] = useState(false);
  const [copied, setCopied] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    setBio(readerData?.resumeBio || '');
  }, [readerData?.resumeBio]);

  const gatherResumeData = async () => {
    if (!readerData?.id) return null;

    // Load reading history
    const histSnap = await getDocs(collection(db, 'readers', readerData.id, 'readingHistory'));
    const readingHistory = [];
    histSnap.forEach(d => readingHistory.push({ id: d.id, ...d.data() }));

    // Load reviews
    const reviewSnap = await getDocs(
      query(collection(db, 'bookReviews'), where('readerEmail', '==', readerData.emailCorrespondence || ''))
    );
    const reviews = [];
    reviewSnap.forEach(d => reviews.push({ id: d.id, ...d.data() }));

    // Load ARC requests
    const arcSnap = await getDocs(
      query(collection(db, 'arcRequests'), where('readerId', '==', readerData.id))
    );
    const arcRequests = [];
    arcSnap.forEach(d => arcRequests.push({ id: d.id, ...d.data() }));

    // Load redemptions
    const redemptionSnap = await getDocs(
      query(collection(db, 'redemptions'), where('readerId', '==', readerData.id))
    );
    const redemptions = [];
    redemptionSnap.forEach(d => redemptions.push({ id: d.id, ...d.data() }));

    // Load import batches to find platforms
    const batchSnap = await getDocs(collection(db, 'readers', readerData.id, 'importBatches'));
    const platformSet = new Set();
    batchSnap.forEach(d => {
      const platform = d.data().platform;
      const info = PLATFORM_INFO[platform];
      if (info) platformSet.add(info.name);
    });

    // Also check sources in reading history
    for (const b of readingHistory) {
      const platform = (b.source || '').replace('-import', '');
      const info = PLATFORM_INFO[platform];
      if (info) platformSet.add(info.name);
    }

    // Enrich reading history with autoTags from bookDatabase
    for (const book of readingHistory) {
      const bid = book.bookId || book.id;
      try {
        const bookDoc = await getDoc(doc(db, 'bookDatabase', bid));
        if (bookDoc.exists()) {
          const data = bookDoc.data();
          book.autoTags = data.autoTags || data.resolvedTags || {};
          if (!book.author && data.author) book.author = data.author;
        }
      } catch (e) { /* non-critical */ }
    }

    return {
      readerName: readerData.name || readerData.firstName || 'Reader',
      memberSince: readerData.signupDate || readerData.selfSignupDate || 'N/A',
      readingHistory,
      reviews,
      arcRequests,
      redemptions,
      totalPoints: readerData.totalPoints || 0,
      socialMedia: readerData.socialMedia || {},
      preferences: readerData.preferences || {},
      platforms: [...platformSet],
      resumeBio: bio || readerData.resumeBio || '',
      verificationCode: generateVerificationCode(readerData.id),
    };
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const data = await gatherResumeData();
      if (!data) { setGenerating(false); return; }

      setPreviewData(data);

      const pdf = generateResumePdf(data);
      const fileName = `Reading_Resume_${(data.readerName || 'Reader').replace(/\s+/g, '_')}.pdf`;
      pdf.save(fileName);
      setGenerated(true);
    } catch (e) {
      console.error('[ReaderResume] Generate error:', e);
      alert('Failed to generate résumé. Please try again.');
    }
    setGenerating(false);
  };

  const handleDownloadAgain = async () => {
    if (!previewData) {
      await handleGenerate();
      return;
    }
    const pdf = generateResumePdf(previewData);
    const fileName = `Reading_Resume_${(previewData.readerName || 'Reader').replace(/\s+/g, '_')}.pdf`;
    pdf.save(fileName);
  };

  const handleSaveBio = async () => {
    if (!readerData?.id) return;
    setSavingBio(true);
    try {
      await updateDoc(doc(db, 'readers', readerData.id), { resumeBio: bio });
    } catch (e) {
      console.error('[ReaderResume] Save bio error:', e);
    }
    setSavingBio(false);
    setEditingBio(false);
  };

  const handleCopyLink = () => {
    const code = generateVerificationCode(readerData?.id);
    const text = `Verified ARC Reader — Narratoria Ltd\nVerification: ${code}\nReader: ${readerData?.name || 'Reader'}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`My Reading Résumé — ${readerData?.name || 'Reader'}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to share my verified reading résumé from Narratoria Ltd.\n\n` +
      `Reader: ${readerData?.name || 'Reader'}\n` +
      `Verification Code: ${generateVerificationCode(readerData?.id)}\n` +
      `Books Read: ${previewData?.readingHistory?.filter(b => b.readStatus === 'read').length || '—'}\n\n` +
      `I've attached my full PDF résumé.\n\nThank you!`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(
      `I just generated my Reading Résumé from @NarratoriaCoop! 📚✨\n\n` +
      `Books read: ${previewData?.readingHistory?.filter(b => b.readStatus === 'read').length || '—'}\n` +
      `Avg rating: ${(() => {
        const rated = (previewData?.readingHistory || []).filter(b => b.rating > 0);
        return rated.length > 0 ? (rated.reduce((s, b) => s + b.rating, 0) / rated.length).toFixed(1) : '—';
      })()}\n\n` +
      `#BookReviewer #ARCReader #Bookstagram`
    );
    window.open(`https://x.com/intent/tweet?text=${text}`, '_blank');
  };

  const verificationCode = generateVerificationCode(readerData?.id);

  return (
    <div className="space-y-4">
      {/* Bio editor */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-slate-700">Your Résumé Bio</p>
          {!editingBio && (
            <button onClick={() => setEditingBio(true)} className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1">
              <Edit3 className="h-3 w-3" /> Edit
            </button>
          )}
        </div>
        {editingBio ? (
          <div className="space-y-2">
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Write a short bio about yourself as a reader — what genres you love, why you read, what makes you a great ARC reviewer..."
              className="text-xs min-h-[80px]"
              maxLength={500}
            />
            <p className="text-[10px] text-slate-400 text-right">{bio.length}/500</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveBio} disabled={savingBio} className="text-xs h-7">
                {savingBio ? 'Saving...' : 'Save Bio'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setEditingBio(false); setBio(readerData?.resumeBio || ''); }} className="text-xs h-7">
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-600">
            {bio || readerData?.resumeBio || 'No bio yet — add one to personalise your résumé.'}
          </p>
        )}
      </div>

      {/* Generate button */}
      {!generated ? (
        <Button
          onClick={handleGenerate}
          disabled={generating}
          className="w-full flex items-center justify-center gap-2"
        >
          <FileText className="h-4 w-4" />
          {generating ? 'Generating Résumé...' : 'Generate My Reading Résumé'}
        </Button>
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
            <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
            <p className="text-sm font-bold text-green-800">Résumé Generated!</p>
            <p className="text-[10px] text-green-700 mt-1">Verification: {verificationCode}</p>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadAgain} className="flex items-center gap-1 text-xs">
              <Download className="h-3.5 w-3.5" /> Download PDF
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopyLink} className="flex items-center gap-1 text-xs">
              {copied ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied!' : 'Copy Details'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleShareTwitter} className="flex items-center gap-1 text-xs">
              <Share2 className="h-3.5 w-3.5" /> Share on X
            </Button>
            <Button variant="outline" size="sm" onClick={handleShareEmail} className="flex items-center gap-1 text-xs">
              <Mail className="h-3.5 w-3.5" /> Email
            </Button>
          </div>

          <Button variant="ghost" size="sm" onClick={() => { setGenerated(false); setPreviewData(null); }} className="w-full text-xs text-slate-500">
            Regenerate
          </Button>
        </div>
      )}

      {/* Preview stats (after generation) */}
      {previewData && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
          <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Résumé Preview</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-sm font-bold text-slate-700">
                {previewData.readingHistory.filter(b => b.readStatus === 'read').length}
              </p>
              <p className="text-[8px] text-slate-500">Books Read</p>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700">
                {previewData.arcRequests.filter(r => r.status === 'approved').length}
              </p>
              <p className="text-[8px] text-slate-500">ARCs Done</p>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-700">{previewData.reviews.length}</p>
              <p className="text-[8px] text-slate-500">Reviews</p>
            </div>
          </div>
          {previewData.platforms.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {previewData.platforms.map(p => (
                <Badge key={p} variant="outline" className="text-[8px]">{p}</Badge>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
